/**
 * Compositional road strip survey — see docs/data-collection/ROAD_SURVEY_STRATEGIES.md
 */

import {
  deriveCarriagewaysFromDivide,
  offsetLineStringMeters,
} from './dividedCarriagewayGeometry';

export type StripCaptureMode =
  | 'trace_centerline'
  | 'trace_outer_kerb'
  | 'derive_from_divide';

/** Physical edge traced for outer-kerb strategy (single strip). Default: property line. */
export type OuterKerbReferenceEdge = 'property';

export const DEFAULT_OUTER_KERB_REFERENCE: OuterKerbReferenceEdge = 'property';

export type RoadSurveyLayout = 'single' | 'divided';

export const STRIP_CAPTURE_MODE_OPTIONS: {
  value: StripCaptureMode;
  label: string;
  hint: string;
}[] = [
  {
    value: 'trace_centerline',
    label: 'Centre line',
    hint: 'Trace the middle of the paved strip. Best for 1–2 lanes.',
  },
  {
    value: 'trace_outer_kerb',
    label: 'Outer kerb',
    hint: 'Trace the outer kerb. Enter the strip width to derive the centreline and lanes.',
  },
  {
    value: 'derive_from_divide',
    label: 'From divide',
    hint: 'No strip trace — use the divide line and entered width for this side.',
  },
];

export function captureModesForLayout(layout: RoadSurveyLayout): StripCaptureMode[] {
  if (layout === 'single') {
    return ['trace_centerline', 'trace_outer_kerb'];
  }
  return ['trace_centerline', 'trace_outer_kerb', 'derive_from_divide'];
}

export function parseStripCaptureMode(raw: unknown): StripCaptureMode {
  if (raw === 'trace_outer_kerb' || raw === 'derive_from_divide') return raw;
  return 'trace_centerline';
}

export function parseDivideWidthM(raw: unknown): number {
  if (raw == null || raw === '') return 0;
  const n = typeof raw === 'number' ? raw : parseFloat(String(raw));
  if (Number.isNaN(n) || n < 0 || n > 80) return 0;
  return n;
}

/**
 * Routing centre from outer kerb trace.
 * Property-side kerb: walk the building line with the carriageway on your right → inward = −hw.
 */
export function deriveCentreFromOuterKerb(
  outer: GeoJSON.LineString,
  widthM: number,
  referenceEdge: OuterKerbReferenceEdge = DEFAULT_OUTER_KERB_REFERENCE,
): GeoJSON.LineString | null {
  if (widthM <= 0 || outer.coordinates.length < 2) return null;
  const inwardM = outerKerbInwardOffsetM(widthM, referenceEdge);
  return offsetLineStringMeters(outer, inwardM);
}

export function outerKerbInwardOffsetM(
  widthM: number,
  referenceEdge: OuterKerbReferenceEdge = DEFAULT_OUTER_KERB_REFERENCE,
): number {
  const hw = widthM / 2;
  void referenceEdge;
  return -hw;
}

export function deriveStripCentreFromDivideSide(
  divide: GeoJSON.LineString,
  widthM: number,
  side: 'a' | 'b',
  divideWidthM = 0,
): GeoJSON.LineString | null {
  if (widthM <= 0) return null;
  const halfBarrier = divideWidthM / 2;
  const hw = widthM / 2;
  const offsetM = side === 'a' ? halfBarrier + hw : -(halfBarrier + hw);
  return offsetLineStringMeters(divide, offsetM);
}

export function resolveDividedStripCentres(payload: {
  divide: GeoJSON.LineString | null;
  divideWidthM: number;
  widthA: number | null;
  widthB: number | null;
  modeA: StripCaptureMode;
  modeB: StripCaptureMode;
  traceA: GeoJSON.LineString | null;
  traceB: GeoJSON.LineString | null;
}): { centreA: GeoJSON.LineString | null; centreB: GeoJSON.LineString | null } {
  const { divide, divideWidthM, widthA, widthB, modeA, modeB, traceA, traceB } = payload;

  let centreA: GeoJSON.LineString | null = null;
  let centreB: GeoJSON.LineString | null = null;

  if (modeA === 'derive_from_divide') {
    if (divide && widthA != null && widthA > 0) {
      centreA = deriveStripCentreFromDivideSide(divide, widthA, 'a', divideWidthM);
    }
  } else if (modeA === 'trace_centerline' && traceA) {
    centreA = traceA;
  } else if (modeA === 'trace_outer_kerb' && traceA && widthA != null && widthA > 0) {
    centreA = deriveCentreFromOuterKerb(traceA, widthA);
  }

  if (modeB === 'derive_from_divide') {
    if (divide && widthB != null && widthB > 0) {
      centreB = deriveStripCentreFromDivideSide(divide, widthB, 'b', divideWidthM);
    }
  } else if (modeB === 'trace_centerline' && traceB) {
    centreB = traceB;
  } else if (modeB === 'trace_outer_kerb' && traceB && widthB != null && widthB > 0) {
    centreB = deriveCentreFromOuterKerb(traceB, widthB);
  }

  return { centreA, centreB };
}

export function stripNeedsMapTrace(mode: StripCaptureMode): boolean {
  return mode === 'trace_centerline' || mode === 'trace_outer_kerb';
}

export function dividedNeedsDivideTrace(
  modeA: StripCaptureMode,
  modeB: StripCaptureMode,
): boolean {
  return modeA === 'derive_from_divide' || modeB === 'derive_from_divide';
}

/** Recompute derived centre previews when both strips derive from divide (M=0 fast path). */
export function previewCentresFromDivideOnly(
  divide: GeoJSON.LineString,
  widthA: number,
  widthB: number,
  divideWidthM: number,
): { a: GeoJSON.LineString; b: GeoJSON.LineString } | null {
  if (divideWidthM > 0) {
    const a = deriveStripCentreFromDivideSide(divide, widthA, 'a', divideWidthM);
    const b = deriveStripCentreFromDivideSide(divide, widthB, 'b', divideWidthM);
    if (!a || !b) return null;
    return { a, b };
  }
  return deriveCarriagewaysFromDivide(divide, widthA, widthB);
}
